import { Input, Button, message, Upload } from 'antd';
import { useEffect, useRef, useState } from 'react';
import EfLink from '@elem/ui/ef-link';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import OSS from 'ali-oss';
import { LoadingOutlined } from '@ant-design/icons';
import { SUPPORT_CASE_TYPE } from '../index';
import { ModalImage } from './Modal';
import { useGlobal } from '@/utils/useGlobal';
import { ENGLISH_LANG, rpcMap, CASE_SUBMIT_KEY, EU_SITE } from '@/constants';
import { createServiceCase } from '@/service';

const { TextArea } = Input;
const ICON = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 14V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V14" stroke="#3F68E0" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 9L12 4M12 4L17 9M12 4V16" stroke="#3F68E0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const HOST_US = 'https://api-a.ecoflow.com';
const HOST_CN = 'https://api-cn.ecoflow.com';
const HOST_EU = 'https://api-e.ecoflow.com';
const HOST_MAP: any = {
    us: HOST_US, // 默认
    cn: HOST_CN,
    de: HOST_EU,
    it: HOST_EU,
    uk: HOST_EU,
    fr: HOST_EU,
    cis: HOST_EU,
    es: HOST_EU,
    eu: HOST_EU,
    za: HOST_EU,
    sa: HOST_EU,
    jp: HOST_EU,
    kr: HOST_EU,
};

export default function Step3({ caseData, onChangeStep, onChangeCase, openModalLimit, user, curDate }: any) {
    const { useLang, gtm } = useGlobal();
    const { locale = 'us' } = useRouter();
    const { t } = useLang('supportcasecreate');
    const recaptchaRef = useRef();
    const [check, setCheck] = useState(true);
    const [fileList, setFileList] = useState<any>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [isSpin, setIsSpin] = useState(false);
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
    const [submitTimes, setSubmitTimes] = useState<any>({});
    const getCdnUrlRule = (path: string) => {
        const urlRule = new RegExp(`.*(?=/${path})`);
        return urlRule;
    };

    const uploaderRequest = async (locale: string) => {
        const HOST_STS = [...EU_SITE, 'za'].includes(locale) ? process.env.NEXT_PUBLIC_STS_HOST_EU : process.env.NEXT_PUBLIC_STS_HOST;
        const info = await (await fetch(`${HOST_STS}/system/oss/fileUpload/stsServer`)).json();
        const { data: config } = await (await fetch(`${HOST_STS}/system/sls/logUpload/prepareSts?scene=FILE_UPLOAD`)).json();
        return {
            region: config?.region,
            bucket: config?.bucket,
            accessKeyId: info.AccessKeyId,
            accessKeySecret: info.AccessKeySecret,
            stsToken: info.SecurityToken,
            cdn: config.cdn,
        };
    };

    const props: any = {
        maxCount: 5,
        multiple: true,
        accept: '.jpg, .jpeg, .png, .doc, .docx, .mp4, .avi',
        listType: 'picture-card',
        name: 'file',
        action: '',
        fileList: fileList,
        headers: {
            authorization: 'authorization-text',
        },
        beforeUpload: (file: any) => {
            // 限制5个文件
            if (fileList.length >= 5) {
                message.error(t('step3_upload_tips1'));
                return false;
            }
            // 限制数量或者10M以内的文件
            if (file.size > 1024 * 1024 * 10) {
                message.error(t('step3_upload_tips2'));
                return false;
            }
            setIsSpin(true);
            return true;
        },
        onPreview: async (file: any) => {
            setPreviewImage(file.url);
            setPreviewOpen(true);
            setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
        },
        onRemove: async (file: any) => {
            const file1 = fileList.filter((item: any) => file.url != item.url);
            setFileList(file1);
        },
        customRequest: async ({ file }: any) => {
            const config = await uploaderRequest(locale);
            const client = new OSS({
                region: config.region,
                refreshSTSTokenInterval: 300000,
                bucket: config.bucket,
                accessKeyId: config.accessKeyId,
                accessKeySecret: config.accessKeySecret,
                stsToken: config.stsToken,
            });
            let folder = '';
            if (['.jpg', '.jpeg', '.png'].includes(file.type)) {
                folder = 'image';
            } else if (['.mp4', '.avi'].includes(file.type)) {
                folder = 'video';
            } else {
                folder = 'doc';
            }
            const url = `upload/${folder}/${Date.now()}/${(file as any).name}`;
            const res = await client.put(url, file);
            if (!res.url) {
                return;
            }
            const fileInfo2: any = {
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: res.url,
            };
            setFileList([...fileList, fileInfo2]);
            setIsSpin(false);
        },
    };

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) return;
        onChangeCase({ recaptchaResponse: captchaCode });
    };

    const onSubmit = async () => {
        // 人机验证、协议勾选
        if (!caseData.recaptchaResponse || !check || !caseData.customerContent) {
            return;
        }
        const currentTimes = submitTimes[user.email] ? submitTimes[user.email] + 1 : 1;
        // 判断当前邮箱三次提交 {date: {email: num}}
        const currentSubmitTimes = { ...submitTimes, [user.email]: currentTimes };
        if (currentTimes > 3) {
            openModalLimit();
            return;
        }
        try {
            // 可上传图片
            if (SUPPORT_CASE_TYPE['Technical'] == caseData.caseTypeCode && fileList.length) {
                const annotationUrls = fileList.map((item: any) => ({ documentUrl: item.url }));
                caseData.annotationUrls = annotationUrls;
            }
            caseData.agreed = check;
            const res = await createServiceCase(caseData, locale);
            if (res?.code == '0') {
                localStorage.setItem(CASE_SUBMIT_KEY, JSON.stringify({ [curDate]: currentSubmitTimes }));
                setSubmitTimes(currentSubmitTimes);
                onChangeStep('success');
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'case_submit',
                    button_name: t('btn_submit'),
                    status: 'success',
                });
            } else {
                message.error(res.message);
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'case_submit',
                    button_name: t('btn_submit'),
                    status: 'fail',
                });
            }
        } catch (error) {}
    };

    useEffect(() => {
        const times = JSON.parse(localStorage.getItem(CASE_SUBMIT_KEY) || '{}');
        setSubmitTimes(times?.[curDate] || {});
    }, []);

    return (
        <div className="step step3">
            <div className="left">
                <div className="icon">
                    <p className="number">{SUPPORT_CASE_TYPE['C&S'] == caseData.caseTypeCode ? 2 : 3}</p>
                </div>
                <div className="text">
                    <div className="step-title">{t('step3')}</div>
                </div>
            </div>
            <div className="form">
                <p className="label">{t('step3_description_label')}</p>
                <TextArea
                    className=""
                    size="large"
                    rows={4}
                    placeholder={SUPPORT_CASE_TYPE['Technical'] == caseData.caseTypeCode ? t('step3_problem_desc2') : t('step3_problem_desc1')}
                    maxLength={500}
                    showCount
                    value={caseData.customerContent}
                    onChange={(e) => onChangeCase({ customerContent: e.target.value })}
                />
                {SUPPORT_CASE_TYPE['Technical'] == caseData.caseTypeCode && (
                    <div className="upload-content">
                        <p className="upload-label">{t('step3_upload_label')}</p>
                        <Upload {...props}>
                            <Button icon={isSpin ? <LoadingOutlined /> : <ICON />}>{t('step3_upload_btn')}</Button>
                        </Upload>
                        <div className="upload-file"></div>
                        <p className="upload-tips">{t('step3_tips')}</p>
                    </div>
                )}
                <div className="g-recaptcha">
                    {/* @ts-ignore */}
                    <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                </div>
                <button className="btn-next" onClick={onSubmit} disabled={!check || !caseData.recaptchaResponse || !caseData.customerContent}>
                    {t('btn_submit')}
                </button>
                <div className="tips-terms">
                    <input type="checkbox" className="agree-check" id="agree" checked={check} onChange={() => setCheck(!check)} />
                    <label htmlFor="agree" className="agree-label">
                        {t.rich('tips_terms', {
                            a1: (text: string) => (
                                <EfLink className="link" href={`https://${locale}.ecoflow.com/pages/terms-of-use`}>
                                    {text}
                                </EfLink>
                            ),
                            a2: (text: string) => (
                                <EfLink className="link" href={`https://${locale}.ecoflow.com/pages/privacy-policy`}>
                                    {text}
                                </EfLink>
                            ),
                        })}
                    </label>
                </div>
            </div>
            <ModalImage open={previewOpen} title={previewTitle} footer={null} url={previewImage} onCancel={() => setPreviewOpen(false)} />
        </div>
    );
}
